// Overview: the store's home page. A welcome band in the brand's maroon with
// today's numbers and the store's status, the setup guide, the store's pulse
// (sales, orders, visitors, conversion with trends), what needs attention,
// best sellers and the storefront. Every number comes from existing APIs
// (analytics dashboard, order counts, low-stock inventory) and each block
// shows only to staff allowed to see it.
import { useMemo, useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowDownRight, ArrowUpRight, Banknote, Boxes, Check, ChevronRight, Copy, CreditCard, ExternalLink, Globe, Lock,
  PackageCheck, Palette, Plus, ShoppingBag, Sparkles,
} from "lucide-react";
import { api, storePath } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { meQuery, useStore, useStoreQuery } from "../lib/store";
import { Badge, Button, Card, cx, Page, SegmentedViews, Skeleton, useToast } from "../components/ui";
import { LineChart, Sparkline } from "../components/charts";
import type { Key } from "../locales/en";
import { NewsCard } from "../components/PlatformBanners";
import { ExploreCard, RemoveDialog, SampleBanner, useSampleData } from "../components/SampleData";

type Guide = { dismissed: boolean; completed: number; total: number; items: { key: string; path: string; status: "done" | "todo" | "coming_soon" }[] };
type Metric = { value: number; compare: number | null; change: number | null };
type Row = { key: string; label?: string; count: number; value: number };
type Point = { day: string; sales: number; orders: number; sessions: number };
type Dashboard = { currency: string; metrics: Record<string, Metric>; series: Point[]; series_previous: Point[]; top_products: Row[] };
type Period = "last_7_days" | "last_30_days" | "last_90_days";

// Date ranges (UTC days, as the analytics dashboard reads them).
const isoDay = (d: Date) => d.toISOString().slice(0, 10);
function presetRange(p: "today" | Period): { from: string; to: string } {
  const now = new Date();
  const today = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
  const back = { today: 0, last_7_days: 6, last_30_days: 29, last_90_days: 89 }[p];
  return { from: isoDay(new Date(today.getTime() - back * 864e5)), to: isoDay(today) };
}

function useDashboard(id: string | undefined, range: { from: string; to: string }, enabled: boolean) {
  return useQuery({
    queryKey: ["store", id, "analytics", "dashboard", range.from, range.to],
    enabled: !!id && enabled,
    placeholderData: (p) => p,
    queryFn: () => api<Dashboard>(storePath(id!, `/analytics/dashboard?from=${range.from}&to=${range.to}`)),
  });
}

// The hour in the store's time zone picks the greeting.
function partOfDay(tz: string): "morning" | "afternoon" | "evening" {
  let h = new Date().getHours();
  try {
    h = Number(new Intl.DateTimeFormat("en-US", { hour: "numeric", hourCycle: "h23", timeZone: tz }).format(new Date()));
  } catch { /* unknown zone: the browser's */ }
  return h < 12 ? "morning" : h < 17 ? "afternoon" : "evening";
}

export function Home() {
  const { id, can } = useStore();
  const me = useQuery(meQuery);
  const sample = useSampleData();
  const [removing, setRemoving] = useState(false);
  const analytics = can("analytics.view");

  const today = useMemo(() => presetRange("today"), []);
  const todayQ = useDashboard(id, today, analytics);

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6 px-4 py-6 sm:px-8 sm:py-8">
      <Welcome
        name={me.data?.name?.split(" ")[0] ?? ""}
        today={analytics ? todayQ.data : undefined}
        loadingToday={analytics && todayQ.isPending}
      />
      <NewsCard />
      <SampleBanner />
      <ExploreCard />
      <SetupGuide onRemoveSample={() => setRemoving(true)} />
      {analytics && <Pulse />}
      <div className="grid items-start gap-6 lg:grid-cols-3">
        {analytics ? <BestSellers className="lg:col-span-2" /> : <div className="hidden lg:col-span-2 lg:block" />}
        <div className="space-y-6">
          {(can("orders.view") || can("inventory.view")) && <Attention />}
          <Storefront />
        </div>
      </div>
      {removing && sample.data && <RemoveDialog status={sample.data} onClose={() => setRemoving(false)} />}
    </div>
  );
}

// ---------- welcome band ----------

function Welcome({ name, today, loadingToday }: { name: string; today?: Dashboard; loadingToday: boolean }) {
  const { t, money, num, date } = useI18n();
  const { handle, summary, can } = useStore();
  const s = summary.data!;
  const greeting = name ? t(`ov.greeting_${partOfDay(s.timezone)}` as Key, { name }) : t("ov.welcome");
  const trialDays = s.subscription.status === "trialing" ? s.subscription.trial_days_left : undefined;
  let dateLine = "";
  try {
    dateLine = date(new Date().toISOString(), { weekday: "long", day: "numeric", month: "long", timeZone: s.timezone });
  } catch {
    dateLine = date(new Date().toISOString(), { weekday: "long", day: "numeric", month: "long" });
  }
  return (
    <section className="relative isolate overflow-hidden rounded-[18px] bg-linear-to-br from-brand-active via-brand to-brand-hover px-6 py-7 text-white shadow-card sm:px-8 sm:py-8">
      {/* The bag mark, large and faint, as the band's watermark. */}
      <img src="/icon-512.png" alt="" aria-hidden className="pointer-events-none absolute -bottom-16 -end-10 -z-10 size-72 select-none opacity-[.13] sm:size-80" />
      <div className="pointer-events-none absolute -start-24 -top-24 -z-10 size-72 rounded-full bg-white/10 blur-3xl" aria-hidden />
      <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <div className="min-w-0 max-w-xl">
          <p className="text-[13px] font-medium text-white/75">{dateLine} · {s.name}</p>
          <h1 className="mt-1.5 text-[28px] font-semibold leading-tight tracking-[-0.02em] sm:text-[32px]">{greeting}</h1>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
            {s.password_protected ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 ring-1 ring-white/20">
                <Lock className="size-3.5" aria-hidden />{t("ov.locked")}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 ring-1 ring-white/20">
                <span className="size-2 rounded-full bg-success-on-dark" aria-hidden />{t("ov.live")}
              </span>
            )}
            {trialDays !== undefined && (
              <Link to="/store/$handle/settings/subscription" params={{ handle }} className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-white/85 ring-1 ring-white/20 hover:bg-white/10">
                <Sparkles className="size-3.5" aria-hidden />{t("ov.trial_left", { days: trialDays })}
              </Link>
            )}
          </div>
          {s.password_protected && <p className="mt-2 text-[13px] text-white/75">{t("ov.locked_hint")}</p>}
          <div className="mt-5 flex flex-wrap gap-2">
            <a href={s.storefront_url} target="_blank" rel="noreferrer"
              className="inline-flex h-9 items-center gap-1.5 rounded-[10px] bg-white px-3.5 text-sm font-medium text-brand transition hover:bg-brand-tint">
              {t("nav.view_store")}<ExternalLink className="size-3.5 rtl:-scale-x-100" aria-hidden />
            </a>
            {s.password_protected && can("settings.general") && (
              <Link to="/store/$handle/settings/store-status" params={{ handle }} className={ghostBtn}>{t("ov.launch")}</Link>
            )}
            {can("products.edit") && (
              <Link to="/store/$handle/products/$productId" params={{ handle, productId: "new" }} className={ghostBtn}><Plus className="size-4" aria-hidden />{t("products.add")}</Link>
            )}
            {can("orders.create") && (
              <Link to="/store/$handle/orders/manual/$draftId" params={{ handle, draftId: "new" }} className={ghostBtn}><ShoppingBag className="size-4" aria-hidden />{t("draft.create")}</Link>
            )}
          </div>
        </div>
        {(today || loadingToday) && (
          <dl className="grid shrink-0 grid-cols-2 gap-px overflow-hidden rounded-[14px] bg-white/15 ring-1 ring-white/20 backdrop-blur-sm">
            {[
              { label: t("ov.today_sales"), value: today ? money(today.metrics.total_sales?.value ?? 0, today.currency) : null },
              { label: t("ov.today_orders"), value: today ? num(today.metrics.orders?.value ?? 0) : null },
            ].map((x) => (
              <div key={x.label} className="min-w-36 bg-brand-active/40 px-5 py-4">
                <dt className="text-xs font-medium text-white/70">{x.label}</dt>
                <dd className="mt-1 text-2xl font-semibold tracking-tight tabular-nums">{x.value ?? <span className="inline-block h-7 w-20 animate-pulse rounded bg-white/20" />}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    </section>
  );
}

const ghostBtn = "inline-flex h-9 items-center gap-1.5 rounded-[10px] px-3.5 text-sm font-medium text-white ring-1 ring-white/30 transition hover:bg-white/10";

// ---------- setup guide ----------

function SetupGuide({ onRemoveSample }: { onRemoveSample: () => void }) {
  const { t } = useI18n();
  const { id, handle, can } = useStore();
  const qc = useQueryClient();
  const sample = useSampleData();
  const guide = useStoreQuery<Guide>(id, "/setup-guide");
  const dismiss = useMutation({
    mutationFn: (dismissed: boolean) => api(storePath(id!, "/setup-guide/dismiss"), { body: { dismissed } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["store", id, "/setup-guide"] }),
  });
  const canEdit = can("settings.general");
  const [all, setAll] = useState(false);
  const g = guide.data;
  if (guide.isPending) return <Card><Skeleton /></Card>;
  if (!g) return null;
  if (g.dismissed || g.completed >= g.total) {
    return g.dismissed && canEdit ? <div><Button variant="plain" onClick={() => dismiss.mutate(false)}>{t("home.guide_show")}</Button></div> : null;
  }
  const next = g.items.find((x) => x.status === "todo")?.key;
  const left = g.items.filter((x) => x.status === "todo").length;
  // The next few steps to do; everything (done, coming soon) on request.
  const todo = g.items.filter((x) => x.status === "todo");
  const shown = all ? g.items : todo.slice(0, 3);
  return (
    <section className="surface overflow-hidden">
      <header className="flex items-center gap-4 border-b border-line bg-brand-tint/50 px-5 py-4">
        <ProgressRing done={g.completed} total={g.total} />
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold text-ink">{t("ov.guide_title")}</h2>
          <p className="text-[13px] text-ink-muted">{t("home.guide_progress", { done: g.completed, total: g.total })} · {t("ov.guide_left", { count: left })}</p>
        </div>
        {canEdit && <Button variant="plain" onClick={() => dismiss.mutate(true)}>{t("home.guide_dismiss")}</Button>}
      </header>
      <ol className="divide-y divide-line">
        {shown.map((it) => {
          const n = g.items.indexOf(it);
          const done = it.status === "done";
          const isNext = it.key === next;
          return (
            <li key={it.key} className={cx("flex items-center gap-3 px-5 py-3", isNext && "bg-brand-tint/40")}>
              <span aria-hidden className={cx("flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
                done ? "bg-brand text-white" : isNext ? "bg-white text-brand ring-2 ring-brand" : "bg-white text-ink-subtle ring-1 ring-line-strong")}>
                {done ? <Check className="size-3.5" strokeWidth={3} /> : n + 1}
              </span>
              <span className={cx("flex-1 text-sm", done ? "text-ink-subtle line-through" : isNext ? "font-medium text-ink" : "text-ink")}>{t(`home.guide.${it.key}` as Key)}</span>
              {it.status === "coming_soon" ? <Badge>{t("common.coming_soon")}</Badge> : it.status === "todo" && (
                it.key === "remove_sample_data"
                  ? canEdit && sample.data?.present && <Button variant={isNext ? "primary" : "secondary"} onClick={onRemoveSample}>{t("sample.remove")}</Button>
                  : <Link to={`/store/$handle${it.path}`} params={{ handle }}><Button variant={isNext ? "primary" : "secondary"}>{t("common.continue")}</Button></Link>
              )}
            </li>
          );
        })}
      </ol>
      {g.items.length > shown.length || all ? (
        <button type="button" onClick={() => setAll(!all)} className="w-full border-t border-line px-5 py-3 text-start text-sm font-medium text-brand hover:bg-brand-tint/40">
          {all ? t("ov.guide_fewer") : t("ov.guide_all", { count: g.items.length })}
        </button>
      ) : null}
    </section>
  );
}

function ProgressRing({ done, total }: { done: number; total: number }) {
  const r = 18, c = 2 * Math.PI * r, f = total ? done / total : 0;
  return (
    <svg viewBox="0 0 44 44" className="size-11 shrink-0 -rotate-90" role="img" aria-label={`${done}/${total}`}>
      <circle cx="22" cy="22" r={r} fill="none" strokeWidth="4" className="stroke-brand-tint-strong" />
      <circle cx="22" cy="22" r={r} fill="none" strokeWidth="4" strokeLinecap="round" className="stroke-brand transition-all"
        strokeDasharray={c} strokeDashoffset={c * (1 - f)} />
      <text x="22" y="22" dy="0.35em" textAnchor="middle" className="rotate-90 fill-ink text-[11px] font-semibold" style={{ transformOrigin: "22px 22px" }}>{done}/{total}</text>
    </svg>
  );
}

// ---------- pulse: KPIs with trends, and the sales chart ----------

function Pulse() {
  const { t, money, num, date } = useI18n();
  const { id, handle } = useStore();
  const [period, setPeriod] = useState<Period>("last_7_days");
  const range = useMemo(() => presetRange(period), [period]);
  const q = useDashboard(id, range, true);
  const d = q.data;
  const tiles: { key: string; label: string; kind: "money" | "int" | "percent"; series?: (p: Point) => number }[] = [
    { key: "total_sales", label: t("an.metric.total_sales"), kind: "money", series: (p) => p.sales },
    { key: "orders", label: t("an.metric.orders"), kind: "int", series: (p) => p.orders },
    { key: "sessions", label: t("ov.visitors"), kind: "int", series: (p) => p.sessions },
    { key: "conversion_rate", label: t("an.metric.conversion_rate"), kind: "percent" },
  ];
  const fmt = (v: number, kind: string) => (kind === "money" ? money(v, d?.currency ?? "USD") : kind === "percent" ? `${num(v)}%` : num(v));
  const label = (day: string) => date(`${day}T12:00:00Z`, { day: "numeric", month: "short", timeZone: "UTC" });
  const points = (d?.series ?? []).map((p, n) => ({ label: label(p.day), value: p.sales, compare: d?.series_previous[n]?.sales }));
  const noSales = d && (d.metrics.total_sales?.value ?? 0) === 0;
  const compact = (n: number) => new Intl.NumberFormat(document.documentElement.lang || "en", { notation: "compact", maximumFractionDigits: 1 }).format(n);
  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-base font-semibold text-ink">{t("ov.pulse")}</h2>
        <div className="flex items-center gap-2">
          <SegmentedViews<Period> label={t("ov.period")} value={period} onChange={setPeriod}
            items={(["last_7_days", "last_30_days", "last_90_days"] as const).map((p) => ({ value: p, label: t(`ov.period.${p}` as Key) }))} />
          <Link to="/store/$handle/analytics" params={{ handle }} className="hidden items-center gap-1 text-sm font-medium text-brand hover:underline sm:inline-flex">
            {t("ov.full_report")}<ChevronRight className="size-4 rtl:-scale-x-100" aria-hidden />
          </Link>
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {tiles.map((x) => {
          const m = d?.metrics[x.key];
          return (
            <div key={x.key} className="surface flex flex-col gap-2 p-5">
              <span className="text-[13px] font-medium text-ink-muted">{x.label}</span>
              {m ? (
                <>
                  <span className="text-2xl font-semibold tracking-tight text-ink tabular-nums">{fmt(m.value, x.kind)}</span>
                  <Trend m={m} />
                  {x.series ? <Sparkline values={d!.series.map(x.series)} className="mt-1" /> : <ConversionBar value={m.value} />}
                </>
              ) : <div className="space-y-2"><div className="h-7 w-24 animate-pulse rounded bg-muted" /><div className="h-9 animate-pulse rounded bg-subtle" /></div>}
            </div>
          );
        })}
      </div>
      <Card title={t("ov.sales_over_time")} description={t("ov.vs_previous")}>
        {!d ? <Skeleton lines={5} /> : noSales ? (
          <div className="flex h-44 flex-col items-center justify-center gap-2 rounded-xl bg-subtle text-center">
            <Banknote className="size-6 text-ink-subtle" aria-hidden />
            <p className="max-w-sm text-sm text-ink-muted">{t("ov.no_sales_yet")}</p>
          </div>
        ) : (
          <LineChart points={points} format={(n) => money(n, d.currency)} axisFormat={compact} label={t("an.this_period")} compareLabel={t("an.previous_period")} height={200} />
        )}
      </Card>
    </section>
  );
}

function Trend({ m }: { m: Metric }) {
  const { t } = useI18n();
  if (m.change === null || m.change === undefined) return <span className="text-xs text-ink-subtle">{t("ov.no_change")}</span>;
  const up = m.change >= 0;
  return (
    <span className="flex items-center gap-1.5 text-xs text-ink-muted">
      <span className={cx("inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 font-medium", up ? "bg-success-bg text-success" : "bg-danger-bg text-danger")}>
        {up ? <ArrowUpRight className="size-3" aria-hidden /> : <ArrowDownRight className="size-3" aria-hidden />}{Math.abs(m.change)}%
      </span>
      {t("ov.vs_previous")}
    </span>
  );
}

function ConversionBar({ value }: { value: number }) {
  return (
    <div className="mt-auto flex h-9 items-end">
      <div className="h-2 w-full overflow-hidden rounded-full bg-brand-tint">
        <div className="h-full rounded-full bg-brand" style={{ width: `${Math.min(100, Math.max(value > 0 ? 2 : 0, value))}%` }} />
      </div>
    </div>
  );
}

// ---------- needs attention ----------

function Attention() {
  const { t, num } = useI18n();
  const { id, handle, can } = useStore();
  const counts = useStoreQuery<Record<string, number>>(id, "/orders/counts", { enabled: can("orders.view") });
  const low = useStoreQuery<{ data: unknown[]; page_info?: { has_next_page?: boolean } }>(id, "/inventory?low_stock=true&limit=20", { enabled: can("inventory.view") });
  const lowCount = low.data ? (low.data.page_info?.has_next_page ? "20+" : num(low.data.data.length)) : null;
  const rows: { key: string; icon: ReactNode; label: string; count: number | string | null; to: ReactNode }[] = [];
  if (can("orders.view")) {
    for (const [tab, icon] of [["unfulfilled", <PackageCheck key="u" className="size-4" />], ["unpaid", <CreditCard key="p" className="size-4" />], ["cod", <Banknote key="c" className="size-4" />]] as const) {
      const n = counts.data?.[tab] ?? null;
      rows.push({ key: tab, icon, label: t(`ov.attention.${tab}` as Key), count: n,
        to: <Link to="/store/$handle/orders" params={{ handle }} search={{ tab }} className="absolute inset-0" aria-label={t(`ov.attention.${tab}` as Key)} /> });
    }
  }
  if (can("inventory.view")) {
    rows.push({ key: "low", icon: <Boxes className="size-4" />, label: t("ov.attention.low_stock"), count: lowCount === "0" ? 0 : lowCount,
      to: <Link to="/store/$handle/inventory" params={{ handle }} className="absolute inset-0" aria-label={t("ov.attention.low_stock")} /> });
  }
  const loading = (can("orders.view") && counts.isPending) || (can("inventory.view") && low.isPending);
  const busy = rows.filter((r) => r.count !== null && r.count !== 0);
  return (
    <section className="surface">
      <header className="px-5 pt-5"><h2 className="text-base font-semibold text-ink">{t("ov.attention")}</h2></header>
      <div className="p-3">
        {loading ? <div className="p-2"><Skeleton lines={3} /></div> : busy.length === 0 ? (
          <div className="flex items-center gap-3 rounded-xl bg-success-bg/60 p-4">
            <span className="flex size-9 items-center justify-center rounded-full bg-success text-white"><Check className="size-4" strokeWidth={3} aria-hidden /></span>
            <div><p className="text-sm font-medium text-ink">{t("ov.caught_up")}</p><p className="text-[13px] text-ink-muted">{t("ov.caught_up_hint")}</p></div>
          </div>
        ) : (
          <ul className="space-y-1">
            {busy.map((r) => (
              <li key={r.key} className="relative flex items-center gap-3 rounded-xl px-2 py-2.5 transition hover:bg-brand-tint/60">
                <span className="flex size-9 items-center justify-center rounded-[10px] bg-brand-tint text-brand" aria-hidden>{r.icon}</span>
                <span className="flex-1 text-sm text-ink">{r.label}</span>
                <span className="font-mono text-sm font-semibold text-ink tabular-nums">{typeof r.count === "number" ? num(r.count) : r.count}</span>
                <ChevronRight className="size-4 text-ink-subtle rtl:-scale-x-100" aria-hidden />
                {r.to}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

// ---------- best sellers ----------

function BestSellers({ className }: { className?: string }) {
  const { t, money } = useI18n();
  const { id } = useStore();
  const range = useMemo(() => presetRange("last_30_days"), []);
  const q = useDashboard(id, range, true);
  const rows = (q.data?.top_products ?? []).slice(0, 5);
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <Card title={t("ov.top_products")} description={t("ov.period.last_30_days")} className={className}>
      {!q.data ? <Skeleton lines={4} /> : rows.length === 0 ? (
        <p className="rounded-xl bg-subtle p-6 text-center text-sm text-ink-muted">{t("ov.top_products_empty")}</p>
      ) : (
        <ol className="space-y-3">
          {rows.map((r, n) => (
            <li key={r.key} className="flex items-center gap-3">
              <span className={cx("flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
                n === 0 ? "bg-brand text-white" : "bg-brand-tint text-brand")}>{n + 1}</span>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="truncate text-sm font-medium text-ink">{r.label || r.key}</span>
                  <span className="shrink-0 text-sm font-semibold text-ink tabular-nums">{money(r.value, q.data!.currency)}</span>
                </div>
                <div className="mt-1.5 flex items-center gap-3">
                  <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-brand-tint">
                    <div className="h-full rounded-full bg-brand" style={{ width: `${(r.value / max) * 100}%` }} />
                  </div>
                  <span className="shrink-0 text-xs text-ink-muted">{t("ov.units", { count: r.count })}</span>
                </div>
              </div>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}

// ---------- storefront ----------

function Storefront() {
  const { t } = useI18n();
  const { handle, summary, can } = useStore();
  const toast = useToast();
  const s = summary.data!;
  const host = s.storefront_url.replace(/^https?:\/\//, "").replace(/\/$/, "");
  const copy = () => {
    void navigator.clipboard?.writeText(s.storefront_url).then(() => toast(t("ov.copied")));
  };
  return (
    <section className="surface overflow-hidden">
      <div className="bg-linear-to-br from-brand-tint to-white px-5 pb-4 pt-5">
        <h2 className="text-base font-semibold text-ink">{t("ov.storefront")}</h2>
        <div className="mt-3 flex items-center gap-2 rounded-[10px] bg-white px-3 py-2 ring-1 ring-line">
          <Globe className="size-4 shrink-0 text-brand" aria-hidden />
          <a href={s.storefront_url} target="_blank" rel="noreferrer" dir="ltr" className="min-w-0 flex-1 truncate font-mono text-[13px] text-ink hover:text-brand">{host}</a>
          <button type="button" onClick={copy} title={t("ov.copy_link")} aria-label={t("ov.copy_link")}
            className="flex size-7 items-center justify-center rounded-md text-ink-muted hover:bg-brand-tint hover:text-brand"><Copy className="size-3.5" /></button>
        </div>
        {s.password_protected && <p className="mt-2"><Badge tone="warning">{t("home.password_protected")}</Badge></p>}
      </div>
      <div className="grid grid-cols-2 gap-px border-t border-line bg-line">
        {can("online_store.themes.view") && (
          <Link to="/store/$handle/storefront/themes" params={{ handle }} className="flex items-center gap-2 bg-white px-5 py-3.5 text-sm font-medium text-ink hover:bg-brand-tint/50 hover:text-brand">
            <Palette className="size-4 text-brand" aria-hidden />{t("ov.customize")}
          </Link>
        )}
        {can("settings.general") && (
          <Link to="/store/$handle/settings/domains" params={{ handle }} className="flex items-center gap-2 bg-white px-5 py-3.5 text-sm font-medium text-ink hover:bg-brand-tint/50 hover:text-brand">
            <Globe className="size-4 text-brand" aria-hidden />{t("ov.domains")}
          </Link>
        )}
      </div>
    </section>
  );
}

export function ComingSoon() {
  const { t } = useI18n();
  return (
    <Page title={t("common.coming_soon")}>
      <Card><p className="text-sm text-stone-600">{t("settings.other")}</p></Card>
    </Page>
  );
}
